import { Injectable } from '@angular/core'
import { CalcMethod, LogisticCategory, RegionServiceConfig, Region, ResolvedService, ProductServiceOverride } from './shipping.interface'

@Injectable({ providedIn: 'root' })
export class ShippingService {
  // ═══════════════════════════════════════════════════════════════════
  // Calculation Methods
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Calculate shipping cost using the given config and product details.
   */
  calculateCost(
    config: RegionServiceConfig | ProductServiceOverride,
    totalCards: number,
    totalWeight: number = 0,
    totalVolume: number = 0,
    logisticCategory?: LogisticCategory
  ): number {
    const method = config.calcMethod
    if (!method) return 0

    switch (method) {
      case 'fixed_per_unit':
        return (config.fixedCost ?? 0) + (config.perUnitCost ?? 0) * totalCards

      case 'fixed_per_range': {
        const range = config.ranges?.find(r => totalCards >= r.min && totalCards <= r.max)
        return range?.cost ?? 0
      }

      case 'fixed_variable_weight':
        return (config.fixedCost ?? 0) + totalWeight * (config.costPerGram ?? 0)

      case 'fixed_variable_volume':
        return (config.fixedCost ?? 0) + totalVolume * (config.costPerCm3 ?? 0)

      case 'fnac_logistic_category': {
        if (!logisticCategory || !config.categoryRates) return 0
        const rates = config.categoryRates[logisticCategory]
        if (!rates) return 0
        return rates.fixedCost + rates.perUnitCost * totalCards
      }

      case 'fixed_variable_range': {
        const value = config.useWeight ? totalWeight : totalVolume
        const range = config.ranges?.find(r => value >= r.min && value <= r.max)
        return range?.cost ?? 0
      }

      default:
        return 0
    }
  }

  /**
   * Resolve the effective rate config for a product in a region+service.
   * Product override wins for rate fields; region metadata is always preserved.
   */
  resolveRateConfig(
    regionService: RegionServiceConfig,
    productOverride?: ProductServiceOverride
  ): RegionServiceConfig {
    if (!productOverride) return { ...regionService }

    return {
      ...regionService,
      ...productOverride,
      // Region-level metadata — never overridden by product
      name: regionService.name,
      active: regionService.active,
      isDefault: regionService.isDefault,
      deliveryDays: regionService.deliveryDays,
      maxWeight: regionService.maxWeight,
      maxVolume: regionService.maxVolume,
      excludedSellers: regionService.excludedSellers,
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // Service Selection
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Get all available services for a product in a region,
   * filtering by active, seller exclusion, and weight/volume limits.
   * Each service has its rate resolved (product override if present).
   */
  getAvailableServices(
    product: any,
    region: Region,
    programNick: string,
    totalWeight: number,
    totalVolume: number
  ): ResolvedService[] {
    const services = region.services || {}

    return Object.entries(services)
      .map(([serviceId, svc]) => ({ serviceId, ...svc }))
      .filter(svc => {
        if (!svc.active) return false
        if (svc.excludedSellers?.includes(product.seller)) return false
        if (svc.maxWeight && totalWeight > svc.maxWeight) return false
        if (svc.maxVolume && totalVolume > svc.maxVolume) return false
        return true
      })
      .map(svc => {
        // Resolve product rate override
        const productOverride: ProductServiceOverride | undefined =
          product.programOverrides?.[programNick]?.shippingPolicies?.[region.id]?.services?.[svc.serviceId]

        const resolvedConfig = this.resolveRateConfig(svc, productOverride)

        const totalCards = 1  // Each card is a separate cart line (qty always 1)
        const resolvedCost = this.calculateCost(
          resolvedConfig,
          totalCards,
          (product.weight ?? 0) * totalCards,
          (product.volume ?? 0) * totalCards,
          product.logisticCategory
        )

        return { ...resolvedConfig, serviceId: svc.serviceId, resolvedCost } as ResolvedService
      })
  }

  /**
   * Select the best service from a list of available services.
   * Priority: preferredId (if still available) → default → first available.
   */
  selectService(services: ResolvedService[], preferredId?: string): ResolvedService | null {
    if (!services.length) return null
    if (preferredId) {
      const preferred = services.find(s => s.serviceId === preferredId)
      if (preferred) return preferred
    }
    return services.find(s => s.isDefault) || services[0]
  }
}
